import {
  type FC,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { Transition } from 'react-transition-group';
import { ThemeProvider } from 'styled-components';
import type { XOR } from 'ts-essentials';

import Icon, { type IconKeys } from '#@/components/Icon';
import { MiniTooltip } from '#@/components/Tooltip';
import useQueryMatches from '#@/shared/hooks/useQueryMatches';
import { COLOR } from '#@/shared/utils/styles';
import { isEntered } from '#@/shared/utils/transitionStates';

import IconClickable from '../IconClickable';
import { MainNavigationContext } from './MainNavigationContext';
import {
  ButtonText,
  CollapseButton,
  Container as ContainerPrimitive,
  CONTAINER_WIDTH_COLLAPSED,
  CONTAINER_WIDTH_EXPANDED,
  CONTAINER_WIDTH_TRANSITION_TIME,
  CustomLogo,
  Divider,
  Logo,
  LogoContainer,
  LogoLink,
  NavButton,
  NavExternalButton,
  NavigationItemsWrapper,
} from './Styles';
import type { MainNavigationTheme } from './theme';

export const DEFAULT_PRIMARY_COLOR = COLOR.brand;
export const DEFAULT_CONTRAST_COLOR = COLOR.white;

export type MainNavigationContainerProps = {
  /** The URL to visit when clicking on the logo */
  mainUrl: string;
  /** URL for custom company logo */
  customLogoUrl?: string;
  /** Value for the `srcSet` attribute for the custom company logo */
  customLogoSrcSet?: string;
  /** Custom "primary color" for the company */
  customBackgroundColor?: string;
  /** Custom "contrast color" for the company */
  customTextColor?: string;
} & (
  | {
      /** If true or undefined, shows the "Collapse"/"Expand" button */
      collapsible?: true;
      isCollapsed: boolean;
      setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
      /** Provide translated string "Collapse", because `cosuno-ui` doesn't contain
       * its own translations (yet?) */
      collapseButtonLabel: string;
      /** Provide translated string "Expand", because `cosuno-ui` doesn't contain
       * its own translations (yet?) */
      expandButtonLabel: string;
    }
  | {
      /** If false, constantly keeps the main navigation open without the button to collapse it */
      collapsible: false;
      isCollapsed?: undefined;
      setIsCollapsed?: undefined;
      collapseButtonLabel?: undefined;
      expandButtonLabel?: undefined;
    }
);

const Container: FC<PropsWithChildren<MainNavigationContainerProps>> = ({
  children,
  customLogoUrl,
  customLogoSrcSet,
  customBackgroundColor,
  customTextColor,
  mainUrl,
  collapsible = true,
  isCollapsed: isCollapsedProp,
  setIsCollapsed,
  collapseButtonLabel,
  expandButtonLabel,
  ...props
}) => {
  const isCollapsed = collapsible ? Boolean(isCollapsedProp) : false;
  const { isMobile } = useQueryMatches();

  const contextValue = useMemo(() => ({ isCollapsed }), [isCollapsed]);

  const closeMenuOnMobile = useCallback(() => {
    if (isMobile) {
      setIsCollapsed?.(true);
    }
  }, [isMobile, setIsCollapsed]);

  return (
    <ThemeProvider
      theme={(theme) =>
        ({
          ...theme,
          mainNavigationBackgroundColor: customBackgroundColor ?? DEFAULT_PRIMARY_COLOR,
          mainNavigationTextColor: customTextColor ?? DEFAULT_CONTRAST_COLOR,
        }) as MainNavigationTheme
      }
    >
      <ContainerPrimitive $isCollapsed={isCollapsed} {...props}>
        <LogoContainer $isCustomLogo={Boolean(customLogoUrl)}>
          <LogoLink data-cy-app-logo to={mainUrl} onClick={closeMenuOnMobile}>
            {customLogoUrl ? (
              <CustomLogo src={customLogoUrl} srcSet={customLogoSrcSet} alt="" />
            ) : (
              <Logo $isCollapsed={isCollapsed} />
            )}
          </LogoLink>
          {isMobile && !isCollapsed && setIsCollapsed && (
            <IconClickable
              type="close"
              size={16}
              onClick={() => setIsCollapsed(true)}
              color={COLOR.white}
            />
          )}
        </LogoContainer>
        <Transition in={!isCollapsed} timeout={CONTAINER_WIDTH_TRANSITION_TIME}>
          {(state) => (
            <NavigationItemsWrapper $showScrollbar={isEntered(state)} $collapsible={collapsible}>
              <MainNavigationContext.Provider value={contextValue}>
                {children}
              </MainNavigationContext.Provider>
            </NavigationItemsWrapper>
          )}
        </Transition>
        {collapsible && setIsCollapsed && !isMobile && (
          <MiniTooltip<HTMLButtonElement>
            isTriggerElementClickable
            bodyText={expandButtonLabel}
            placement="right"
            offsetValue={12}
          >
            {({ ref, getReferenceProps }) => (
              <CollapseButton
                {...(isCollapsed
                  ? {
                      ref,
                      ...getReferenceProps({
                        onClick: () => setIsCollapsed((currentState) => !currentState),
                      }),
                    }
                  : {
                      onClick: () => setIsCollapsed((currentState) => !currentState),
                    })}
              >
                <Icon size={24} type={isCollapsed ? 'chevron-right' : 'chevron-left'} />
                <ButtonText $isCollapsed={isCollapsed}>{collapseButtonLabel}</ButtonText>
              </CollapseButton>
            )}
          </MiniTooltip>
        )}
      </ContainerPrimitive>
    </ThemeProvider>
  );
};

type MainNavigationItemProps = HTMLAttributes<HTMLAnchorElement> & {
  label: string;
} & XOR<{ iconType: IconKeys }, { renderIcon: (size: number) => ReactElement }>;

interface LocalItemProps {
  url: string;
}

interface ExternalItemProps {
  externalUrl: string;
}

const Item: FC<MainNavigationItemProps & XOR<LocalItemProps, ExternalItemProps>> = ({
  url,
  externalUrl,
  label,
  iconType,
  onClick,
  renderIcon,
  ...props
}) => {
  const { isCollapsed } = useContext(MainNavigationContext);
  const iconSize = 20;

  const renderNavButtonChildren = () => (
    <>
      {renderIcon ? renderIcon(iconSize) : <Icon size={iconSize} type={iconType} />}
      <ButtonText $isCollapsed={isCollapsed}>{label}</ButtonText>
    </>
  );

  return (
    <MiniTooltip<HTMLAnchorElement>
      key={url ?? externalUrl}
      bodyText={label}
      placement="right"
      offsetValue={12}
      isTriggerElementClickable
    >
      {({ ref, getReferenceProps }) => (
        <>
          {url && (
            <NavButton
              {...props}
              {...(isCollapsed
                ? {
                    ref,
                    ...getReferenceProps({
                      onClick: (event: MouseEvent<HTMLAnchorElement>) => onClick?.(event),
                    }),
                  }
                : {
                    onClick: (event) => onClick?.(event),
                  })}
              to={url}
            >
              {renderNavButtonChildren()}
            </NavButton>
          )}
          {externalUrl && (
            <NavExternalButton
              {...props}
              {...(isCollapsed
                ? {
                    ref,
                    ...getReferenceProps({
                      onClick: (event: MouseEvent<HTMLAnchorElement>) => onClick?.(event),
                    }),
                  }
                : {
                    onClick: (event) => onClick?.(event),
                  })}
              to={externalUrl}
            >
              {renderNavButtonChildren()}
            </NavExternalButton>
          )}
        </>
      )}
    </MiniTooltip>
  );
};

export const MainNavigation = {
  Container,
  Item,
  Divider,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_CONTRAST_COLOR,
  CONTAINER_WIDTH_COLLAPSED,
  CONTAINER_WIDTH_EXPANDED,
  CONTAINER_WIDTH_TRANSITION_TIME,
};
